import React, { useRef, useEffect, useState } from 'react';
import { Send, Circle, ChevronUp, ChevronDown } from 'react-bootstrap-icons';
import IntentSelection from './IntentSelection'; // Make sure to save the previous component file

const ChatInterface = ({
  messages,
  isLoading,
  inputValue,
  setInputValue,
  handleSendMessage, // Ensure parent function accepts (text, isHidden)
  tasks,
  isTasksExpanded,
  setIsTasksExpanded,
  handleCompleteTask,
  weeklyLimitReached,
  showSettings,
  setShowSettings,
  canChangeMentor,
  isChanging,
  initiateChangeMentor,
  nextChangeDate
}) => {
  const messagesEndRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  // 1. Check if conversation already exists (User returning)
  useEffect(() => {
    if (messages.length > 0) {
      setHasStarted(true);
    }
  }, [messages]);

  // 2. Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // 3. Handler for Intent Selection
  const onIntentSelect = (backendPrompt) => {
    setHasStarted(true); 
    handleSendMessage(backendPrompt, true); 
  };

  if (!hasStarted && messages.length === 0) {
    return <IntentSelection onSelect={onIntentSelect} />;
  }

  if (hasStarted && messages.length === 0 && isLoading) {
    return (
      <div className="loading-screen fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px', textAlign: 'center' }}>
         <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #9773e4', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '20px' }}></div>
         <h3 style={{color: '#555'}}>Connecting to your Mentor...</h3>
         <p style={{color: '#888'}}>Analyzing your intent and preparing the session.</p>
         <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // --- VIEW 3: MAIN CHAT INTERFACE ---
  return (
    <div className="chat-interface fade-in">

      {/* Settings Panel */}
      {showSettings && (
        <div className="mentor-settings-panel fade-in" style={{ marginBottom: '15px', padding: '15px', background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#555' }}>Mentor Settings</span>
              <button onClick={() => setShowSettings(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#999', fontSize: '1.1rem' }}>✕</button>
          </div>

          {!canChangeMentor && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '10px' }}>You recently switched mentors.</p>
              <div style={{ background: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7', borderRadius: '6px', padding: '8px 12px', fontSize: '0.85rem', fontWeight: '600', display: 'inline-block', width: '100%' }}>
                  🔒 Locked until {nextChangeDate ? new Date(nextChangeDate).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          )}

          {canChangeMentor && (
            <div>
              <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '8px' }}>Warning: Changing mentors will clear your chat history.</p>
              <button 
                  onClick={initiateChangeMentor}
                  disabled={isChanging}
                  style={{ width: '100%', padding: '8px', background: '#fff', color: '#d63384', border: '1px solid #d63384', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}
              >
                  {isChanging ? 'Switching...' : '🔄 Change Mentor'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Task Widget */}
      {tasks.length > 0 && (
        <div className={`task-widget-container ${isTasksExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="task-widget-header" onClick={() => setIsTasksExpanded(!isTasksExpanded)}>
            <div className="task-header-title"><span>🎯 Goals & Tasks ({tasks.length})</span></div>
            <div className="task-header-icon">{isTasksExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}</div>
          </div>

          {isTasksExpanded && (
              <div className="task-list fade-in">
                {tasks.map(task => (
                  <div key={task.id} className="task-item">
                    <button className="task-check-btn" onClick={() => handleCompleteTask(task.id)} title="Mark as Done"><Circle size={18} /></button>
                    <span className="task-text">{task.title}</span>
                  </div>
                ))}
              </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="message-container">
        {messages.filter(msg => typeof msg.content === 'string' && !msg.content.startsWith("CONTEXT:")) 
        .map((msg, index) => (
          <div key={index} className={`message ${msg.role === 'user' ? 'user' : 'bot'}`}>
            <span dangerouslySetInnerHTML={{ __html: msg.content }} />
          </div>
        ))}
        {isLoading && (
          <div className="message bot">
            <span className="typing-dot"></span><span className="typing-dot"></span><span className="typing-dot"></span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {!weeklyLimitReached ? (
          <div className="chat-input-area">
              <input 
                  type="text" 
                  placeholder="Type a message..." 
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={isLoading} 
              />
              <button className="send-btn" onClick={() => handleSendMessage()} disabled={isLoading}><Send size={18} /></button>
          </div>
      ) : (
          <div className="chat-input-area locked" style={{ background: '#fff3cd', borderTop: '1px solid #ffeeba', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
              <div style={{ textAlign: 'center', color: '#856404' }}>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.9rem' }}>⛔ Weekly Session Limit Reached</p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem' }}>You have used all 4 sessions for this week. <br/>Please come back next week to continue your progress!</p>
              </div>
          </div>
      )}
    </div>
  );
};

export default ChatInterface;